import React from "react";
import PropTypes from "prop-types";
import { debounce } from "lodash";

import { gatherClasses, isFunction } from "../../lib/utils";
import Button from "../button";
import Label from "../label";
import Input from "../input";
import "./pagination.scss";

class Pagination extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      inputPageNo: props.currentPage,
      totalPages: props.totalRecords
        ? Pagination.calculatePagesCount(
            props.recordsPerPage,
            props.totalRecords
          )
        : null
    };
    this.debouncedOnPageChange = debounce(this.debouncedOnPageChange, 1000);
  }

  static getDerivedStateFromProps(props, state) {
    const newState = {
      inputPageNo: props.currentPage,
      totalPages: Pagination.calculatePagesCount(
        props.recordsPerPage,
        props.totalRecords
      )
    };

    if (state.inputPageNo === "" || state.inputPageNo !== props.currentPage) {
      newState.inputPageNo = state.inputPageNo;
    }

    return newState;
  }

  static calculatePagesCount(recordsPerPage, totalRecords) {
    return totalRecords < recordsPerPage
      ? 1
      : Math.ceil(totalRecords / recordsPerPage);
  }

  handlePageChange = dir => () => {
    const { totalPages } = this.state;
    const { onPageChange, currentPage } = this.props;
    const nextPage =
      dir === "prev"
        ? Math.max(1, currentPage - 1)
        : totalPages
        ? Math.min(totalPages, currentPage + 1)
        : currentPage + 1;

    onPageChange(nextPage);
  };

  debouncedOnPageChange = nextPage => {
    const { inputPageNo } = this.state;
    const { onPageChange } = this.props;

    if (inputPageNo) {
      onPageChange(nextPage);
    }
  };

  handlePageInputChange = e => {
    const { totalPages } = this.state;
    const intPage = parseInt(e.target.value, 10);
    const nextPage =
      intPage < 1 ? 1 : intPage > totalPages ? totalPages : intPage;

    if (nextPage) {
      this.debouncedOnPageChange(nextPage);
      this.setState({ inputPageNo: nextPage });
    } else {
      this.setState({ inputPageNo: e.target.value });
    }
  };

  getInfoText = () => {
    const { recordsPerPage, totalRecords, currentPage, getInfoText } =
      this.props;
    let from = currentPage;
    let to = recordsPerPage;

    if (currentPage > 1) {
      from = (currentPage - 1) * recordsPerPage + 1;
      to = Math.min(totalRecords, from + (recordsPerPage - 1));
    }

    if (isFunction(getInfoText)) {
      return getInfoText(from, to, totalRecords);
    }

    return `${from}-${to} of ${totalRecords}`;
  };

  render() {
    const { totalPages, inputPageNo } = this.state;
    const { currentPage, totalRecords, showFooterInfo, alignLeft } = this.props;

    return (
      <div
        className={gatherClasses(
          this.props,
          "oui-pagination-container",
          "oui-d-flex",
          alignLeft ? "oui-justify-content-start" : "oui-justify-content-end",
          "oui-align-items-center",
          "oui-py-2"
        )}
      >
        {totalRecords > 0 && showFooterInfo && (
          <Label text={this.getInfoText()} className="oui-mr-3 oui-mb-0" />
        )}
        <Button
          disabled={currentPage <= 1}
          dataTestId="pagination-prev-btn"
          icon={{
            svg: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="16"
                fill="none"
                viewBox="3 0 10 18"
                color="currentColor"
                className="orion-icons-svg-base orion-icons-svg-current-clr orion-icons-svg-muted"
              >
                <path
                  fill="#6C757D"
                  d="M10.988 14c-.2 0-.398-.05-.547-.199L5.224 8.534a.72.72 0 010-1.043l5.267-5.267a.72.72 0 011.043 0 .72.72 0 010 1.043l-4.72 4.72 4.72 4.721a.72.72 0 010 1.044.798.798 0 01-.546.248z"
                />
              </svg>
            )
          }}
          type="secondary"
          size="sm"
          onClick={this.handlePageChange("prev")}
        />
        <Button
          icon={{
            svg: (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="16"
                fill="none"
                viewBox="2 0 10 18"
                color="currentColor"
                className="orion-icons-svg-base orion-icons-svg-current-clr orion-icons-svg-muted"
              >
                <path
                  d="M5.77259 14C5.57321 14 5.37383 13.9502 5.2243 13.8006C4.92523 13.5016 4.92523 13.053 5.2243 12.7539L9.9595 7.96885L5.2243 3.28349C4.97508 2.98442 4.97508 2.48598 5.2243 2.18692C5.52336 1.93769 6.02181 1.93769 6.32087 2.18692L11.5545 7.47041C11.8536 7.76947 11.8536 8.21807 11.5545 8.51713L6.32087 13.7508C6.17134 13.9003 5.97196 14 5.77259 14Z"
                  fill="#6C757D"
                />
              </svg>
            )
          }}
          dataTestId="pagination-next-btn"
          type="secondary"
          size="sm"
          disabled={!!(totalPages && totalPages === currentPage)}
          onClick={this.handlePageChange("next")}
          className="oui-mr-3"
        />
        <Label text="Jump to: " className="oui-mb-0" />
        <Input
          type="number"
          min="1"
          max={totalPages || undefined}
          className="oui-ml-1 oui-pagination-page-input"
          value={inputPageNo}
          onChange={this.handlePageInputChange}
        />
      </div>
    );
  }
}

Pagination.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  className: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  recordsPerPage: PropTypes.number,
  totalRecords: PropTypes.number,
  onPageChange: PropTypes.func,
  showFooterInfo: PropTypes.bool,
  alignLeft: PropTypes.bool,
  getInfoText: PropTypes.func
};

Pagination.defaultProps = {
  recordsPerPage: 10,
  showFooterInfo: true,
  alignLeft: false
};

export default Pagination;
